package sarka.design.skoria.navigation

/**
 * String-based navigation destinations, all argument-free.
 *
 * On the Compose/JS (web) target in this project's navigation version, a
 * destination that declares navigation arguments fails to resolve: navigating to
 * it throws `IllegalArgumentException: Navigation destination that matches route
 * ... cannot be found in the navigation graph`, even though the same code works
 * on Android. Argument-free destinations resolve reliably on every target.
 *
 * So destinations that need a parameter (the conversation screen's target user,
 * the verify-email username/email) don't carry it in the route. The value is
 * stashed in [NavArgs] immediately before navigating and read back by the
 * destination. See [NavArgs] for the trade-offs.
 */
object Routes {
    const val LOGIN = "login"
    const val REGISTER = "register"
    const val HOME = "home"
    const val CHAT = "chat"
    const val MESSAGES = "messages"
    const val VERIFY_EMAIL = "verifyEmail"
    const val CONVERSATION = "conversation"
}

/**
 * Holds the parameters for the next argument-free destination.
 *
 * This exists because navigation arguments don't resolve on web (see [Routes]).
 * Rather than encode values into the route, the navigator sets them here and the
 * destination reads them on first composition.
 *
 * Trade-offs, accepted deliberately:
 *  - Not restored across a full page reload / process death. A reload drops the
 *    user back to a parameter-less screen, which for a conversation means the
 *    friends list is the safe fallback. This matches the previous behaviour and
 *    is acceptable for these screens.
 *  - Single-slot per parameter: only the most recent target matters, since a
 *    destination reads its value as it's created and keys its ViewModel on it.
 */
object NavArgs {
    /** Target user for the conversation screen. */
    var conversationWith: String? = null

    /** Username and email for the verify-email screen; email may be blank. */
    var verifyEmailUsername: String? = null
    var verifyEmailEmail: String = ""
}
